/**
 * RoleRouteGuard - Prevents unauthorized role access to routes.
 * Redirects to dashboard if the user's role is not in the allowed list.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEffectivePermissions } from '@/hooks/useEffectivePermissions';
import type { RoleId } from '@/config/roles';

interface RoleRouteGuardProps {
 children: React.ReactNode;
 allowedRoles: RoleId[];
 fallbackRoute?: string;
}

export const RoleRouteGuard: React.FC<RoleRouteGuardProps> = ({
 children,
 allowedRoles,
 fallbackRoute = '/dashboard',
}) => {
 const { effectiveRole, isActualGlobalAdmin } = useEffectivePermissions();
 const navigate = useNavigate();
 const [checked, setChecked] = useState(false);

 useEffect(() => {
 if (!effectiveRole) return; // Still loading

 // Global admins can always access (they may be simulating a role)
 if (isActualGlobalAdmin) {
 setChecked(true);
 return;
 }

 if (!allowedRoles.includes(effectiveRole as RoleId)) {
 console.warn(`[RoleRouteGuard] Role "${effectiveRole}" denied access. Allowed: ${allowedRoles.join(', ')}`);
 navigate(fallbackRoute, { replace: true });
 } else {
 setChecked(true);
 }
 }, [effectiveRole, isActualGlobalAdmin, allowedRoles, navigate, fallbackRoute]);

 if (!checked) {
 return (
 <div className="flex items-center justify-center h-64">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
 </div>
 );
 }

 return <>{children}</>;
};
